import React, { ReactNode } from 'react';
import Button from '@bankiru/ui-kit/core/Button';

import { isMobilePlatform } from '../../../helpers/isMobilePlatform';
import { ConsentKeys } from '../../../types/consents';
import { Platform } from '../../../types/platforms';
import { SOCIAL_BUTTON_PROPS } from './constants';
import { SocialProvider, SocialProvidersType } from './types';

type GetSocialButtonProps = {
	provider: SocialProvider;
	checkedConsents: Record<ConsentKeys, boolean>;
	trackYM: (id: string, label?: string) => void;
	isDisabled: boolean;
	isWithoutLabel: boolean;
	platform: Platform;
};

export const getSocialButton = ({
	provider,
	checkedConsents,
	trackYM,
	isDisabled,
	isWithoutLabel,
	platform,
}: GetSocialButtonProps): ReactNode => {
	const socialButtonProps = SOCIAL_BUTTON_PROPS[provider.alias as SocialProvidersType];

	const consentsState = Object.keys(checkedConsents) as ConsentKeys[];
	const consents = consentsState.map(key => `${key}=${checkedConsents[key] ? '1' : 0}`);

	const handleClick = () => {
		trackYM(SOCIAL_BUTTON_PROPS[provider.alias as SocialProvidersType].ymEventAction, platform);
	};

	const buttonLabel = isWithoutLabel ? socialButtonProps.label : '';

	if (socialButtonProps) {
		return (
			<Button
				size="h48"
				skin="white"
				leftIcon={{
					size24: <img src={socialButtonProps.icon} width="24" height="24" alt="" />,
					size16: <img src={socialButtonProps.icon} width="24" height="24" alt="" />,
				}}
				key={provider.alias}
				href={`${provider.loginUrl}&${consents.join('&')}`}
				isDisabled={isDisabled}
				isSpaceBetweenIcon={false}
				isFullWidth={true}
				onClick={handleClick}
			>
				{buttonLabel}
			</Button>
		);
	}

	return (
		<Button size="h48" skin="white" href={provider.loginUrl} isDisabled={isDisabled}>
			{provider.displayName}
		</Button>
	);
};

export const sortSocialProviders = (
	providers: SocialProvider[],
	isIOS: boolean,
	platform: Platform,
) =>
	providers.reduce(
		(acc, provider) => {
			if (
				(isMobilePlatform(platform) &&
					SOCIAL_BUTTON_PROPS[provider.alias as SocialProvidersType]
						?.isHiddenMobilePlatform) ||
				(isIOS && SOCIAL_BUTTON_PROPS[provider.alias as SocialProvidersType]?.isHiddenIOS)
			) {
				return { ...acc };
			}

			if (SOCIAL_BUTTON_PROPS[provider.alias as SocialProvidersType]?.isOther) {
				return {
					mainSocialProviders: acc.mainSocialProviders,
					otherSocialProviders: [
						...acc.otherSocialProviders,
						provider,
					] as SocialProvider[],
				};
			}

			return {
				mainSocialProviders: [...acc.mainSocialProviders, provider] as SocialProvider[],
				otherSocialProviders: acc.otherSocialProviders,
			};
		},
		{
			mainSocialProviders: [] as SocialProvider[],
			otherSocialProviders: [] as SocialProvider[],
		},
	);
